import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import currency from "currency.js";
import SearchIcon from "@mui/icons-material/Search";
import { UserContext } from "../../context/UserContext";
import BDatePicker from "../../common/input/BDatePicker";
import { useLocation } from "react-router-dom";
import { getQueryParam } from "../../common/util";
import { useNavigate } from "react-router";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const PAYOUT_COLUMNS = [
  {
    field: "payoutDate",
    headerName: "Payout Date",
    type: "number",
    renderCell: (params) => (
      <div>
        <Box py={2}>
          <Typography variant="body2" display="block">
            {moment(params.value).format("ll")}
          </Typography>
        </Box>
      </div>
    ),
    width: 120,
  },
  {
    field: "amount",
    headerName: "Amount",
    type: "number",
    valueFormatter: ({ value }) => currency(value, { fromCents: true }).format(),
    width: 120,
  },
  {
    field: "status",
    headerName: "Status",
    width: 100,
    renderCell: (params) => (
      <Chip
        size="small"
        label={params.value}
        color={params.value === "paid" ? "success" : "secondary"}
      />
    ),
  },
  {
    field: "settlementDate",
    headerName: "Available On",
    type: "datetime",
    valueGetter: ({ value }) => value && new Date(value),
    valueFormatter: ({ value }) => moment(value).add(12, "hours").format("ll"),
    width: 120,
  },
];

const PAYOUT_DETAILS_COLUMNS = [
  {
    field: "time",
    headerName: "Timestamp",
    type: "number",
    renderCell: (params) => (
      <div>
        <Box py={1}>
          <Typography variant="caption" display="block">
            {moment(params.value).format("ll")}
          </Typography>
          <Typography variant="overline" display="block" color="textSecondary">
            {moment(params.value).format("LT")}
          </Typography>
        </Box>
      </div>
    ),
    width: 120,
  },
  {
    field: "grossAmount",
    headerName: "Gross Amount",
    type: "number",
    valueFormatter: ({ value }) => currency(value, { fromCents: true }).format(),
    width: 120,
  },
  {
    field: "fee",
    headerName: "Fee",
    type: "number",
    valueFormatter: ({ value }) => currency(value, { fromCents: true }).format(),
    width: 120,
  },
  {
    field: "netAmount",
    headerName: "Net Amount",
    type: "number",
    valueFormatter: ({ value }) => currency(value, { fromCents: true }).format(),
    width: 120,
  },
];

export default function SalesReport(props) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  let location = useLocation();

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [payouts, setPayouts] = useState([]);

  const [totalAmmount, setTotalAmount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const apiRef = useRef(null);
  const transactionsApiRef = useRef(null);

  // Check params to identify search criteria
  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      // reset dates
      qFrom = moment().subtract(7, "days").format("YYYY-MM-DD");
      qTo = moment().format("YYYY-MM-DD");
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    } else if (fromDt && toDt && moment(toDt).diff(moment(fromDt), "days") > 31) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/report/payout?from=${fromDt}&to=${toDt}`);
  };

  const search = (fromDate, toDate, withTransactions = false) => {
    setLoading(true);
    bkstApi
      .get(
        `/my/report/payout?fromDate=${fromDate}&toDate=${toDate}&withTransactions=${withTransactions}`
      )
      .then((res) => {
        setPayouts(res.data.payouts);

        let [count, total] = [0, 0];
        for (const o of res.data.payouts) {
          count++;
          total += +o.amount;
        }
        setTotalCount(count);
        setTotalAmount(total);

        setFromDt(fromDate);
        setToDt(toDate);

        setLoading(false);
      });
  };

  const exportToCSV = () => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv({
        fileName: `Payout_Report_${fromDt}_${toDt}`,
        fields: ["payoutDate", "amount", "status", "settlementDate"],
      });
    }
  };

  const exportTransactionsToCSV = () => {
    if (transactionsApiRef.current) {
      transactionsApiRef.current.exportDataAsCsv({
        fileName: `Payout_Transactions_${fromDt}_${toDt}`,
        fields: ["time", "grossAmount", "fee", "netAmount"],
      });
    }
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Box>
        <Container maxWidth="md">
          <Box>
            <Box mt={4}>
              <Typography variant="h1" gutterBottom>
                Payouts Report
              </Typography>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                Your payments are aggregated and the payouts are sent on a 2-day rolling basis.
              </Typography>
              <Box my={3}>
                <form onSubmit={runDateSearch}>
                  <Box sx={{ display: "flex", gap: "10px" }}>
                    <BDatePicker
                      value={fromDt}
                      variant="filled"
                      label="From Date"
                      onChange={(d) => setFromDt(d)}
                      required
                      autoOk
                      disableFuture
                      hideIcon={true}
                    />
                    <BDatePicker
                      value={toDt}
                      variant="filled"
                      label="To Date"
                      onChange={(d) => setToDt(d)}
                      required
                      autoOk
                      disableFuture
                      hideIcon={true}
                      minDate={fromDt ? moment(fromDt).toDate() : undefined}
                      maxDate={fromDt ? moment(fromDt).add(31, "days").toDate() : undefined}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      style={{ height: "56px" }}
                    >
                      <SearchIcon />
                    </Button>
                  </Box>
                </form>
              </Box>
            </Box>
          </Box>
          {payouts && payouts.length > 0 && (
            <Box mt={2} mb={4}>
              <Grid container spacing={2}>
                <Grid item xs={6} align="center">
                  <Box px={2} py={4} style={{ backgroundColor: "#ECECEC" }}>
                    <Typography variant="h4">
                      {currency(totalAmmount, { fromCents: true }).format()}
                    </Typography>
                    <Typography variant="overline" color="textSecondary">
                      TOTAL AMOUNT
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} align="center">
                  <Box px={2} py={4} style={{ backgroundColor: "#ECECEC" }}>
                    <Typography variant="h4">{totalCount}</Typography>
                    <Typography variant="overline" color="textSecondary">
                      PAYOUTS
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          {payouts && payouts.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h4" gutterBottom>
                Payouts
              </Typography>
              <IconButton onClick={exportToCSV}>
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={{ width: "100%", display: "grid" }}>
            <DataGrid
              apiRef={apiRef}
              rows={payouts.map((o) => {
                return {
                  id: o.id,
                  payoutDate: o.created * 1000,
                  amount: o.amount,
                  status: o.status,
                  settlementDate: o.arrival_date * 1000,
                  automatic: o.automatic,
                };
              })}
              columns={PAYOUT_COLUMNS}
              autoHeight
              getRowHeight={(params) => "auto"}
              disableRowSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [{ field: "payoutDate", sort: "desc" }],
                },
              }}
            />
          </Box>
          {payouts.some((payout) => payout.transactions) ? (
            <Box>
              <Box
                mt={4}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <Typography variant="h4" gutterBottom>
                  Transactions
                </Typography>
                <IconButton onClick={exportTransactionsToCSV}>
                  <FileDownloadOutlinedIcon />
                </IconButton>
              </Box>
              <Box sx={{ width: "100%", display: "grid" }}>
                <DataGrid
                  apiRef={transactionsApiRef}
                  rows={payouts.flatMap((payout) =>
                    (payout.transactions || []).map((o) => {
                      return {
                        id: o.id,
                        time: o.created * 1000,
                        grossAmount: o.amount,
                        fee: o.fee,
                        netAmount: o.net,
                        description: o.description,
                        type: o.type,
                        link: o.orderId
                          ? `/order/${o.orderId}`
                          : o.reqId
                            ? `/redirect?from=quote&id=${o.reqId}&to=order`
                            : "",
                      };
                    })
                  )}
                  columns={PAYOUT_DETAILS_COLUMNS}
                  autoHeight
                  getRowHeight={(params) => "auto"}
                  disableRowSelectionOnClick
                  initialState={{
                    sorting: {
                      sortModel: [{ field: "time", sort: "desc" }],
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box mt={4}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => search(fromDt, toDt, true)}
              >
                Get Detailed Report
              </Button>
            </Box>
          )}
        </Container>
      </Box>
    );
  }
}
